import { Link } from '@tanstack/react-router';

import { NAV_GROUPS, type NavItem } from '@/config/navigation';
import { useOperator } from '@/features/auth/operator-context';
import { ENTITY_KIND_STYLE, useRecentEntities } from '@/features/recents/recent-entities';
import { cn } from '@/lib/cn';

interface SidebarNavProps {
  /** Collapsed rail shows icons only. */
  expanded: boolean;
  /** Mobile drawer closes on navigate; the persistent sidebar does not. */
  onNavigate?: () => void;
}

export function SidebarNav({ expanded, onNavigate }: SidebarNavProps) {
  const { can } = useOperator();
  const recents = useRecentEntities();

  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => can(item.capability)),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex-1 overflow-x-hidden overflow-y-auto px-2 py-2.5">
      {groups.map((group, index) => (
        <div key={group.label ?? `group-${String(index)}`} className="mb-3.5">
          {group.label && expanded && (
            <div
              className="font-head text-tx3 px-2 pb-[7px] text-[11px] font-semibold uppercase"
              style={{ letterSpacing: '0.18em' }}
            >
              {group.label}
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <SidebarLink
                key={item.to}
                item={item}
                expanded={expanded}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      ))}

      {expanded && recents.length > 0 && (
        <div className="border-bd mt-1 border-t pt-3">
          <div
            className="font-head text-tx3 px-2 pb-[7px] text-[11px] font-semibold uppercase"
            style={{ letterSpacing: '0.18em' }}
          >
            Recent
          </div>
          <div className="flex flex-col gap-px">
            {recents.map((recent) => {
              const style = ENTITY_KIND_STYLE[recent.kind];
              return (
                <Link
                  key={`${recent.kind}:${recent.id}`}
                  to={recent.to}
                  onClick={onNavigate}
                  className="hover:bg-elev flex w-full items-center gap-2 rounded-md px-2 py-[5px] text-left no-underline hover:no-underline"
                >
                  <span
                    aria-hidden
                    className="grid size-[18px] flex-none place-items-center rounded font-mono text-[9px] font-bold"
                    style={{ background: style.tint, color: style.tintFg }}
                  >
                    {style.tag}
                  </span>
                  <span className="text-tx2 flex-1 truncate text-xs">{recent.name}</span>
                  <span className="sr-only">{style.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarLink({
  item,
  expanded,
  onNavigate,
}: {
  item: NavItem;
  expanded: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      title={item.label}
      activeOptions={{ exact: item.to === '/' }}
      className="group hover:bg-elev relative flex h-9 w-full items-center gap-2.5 rounded-[7px] px-2 text-left no-underline hover:no-underline"
      activeProps={{ style: { background: 'var(--high)' } }}
    >
      {({ isActive }: { isActive: boolean }) => (
        <>
          <span
            aria-hidden
            className="absolute top-2 bottom-2 left-0 w-0.5 rounded-sm"
            style={{ background: isActive ? 'var(--brand)' : 'transparent' }}
          />
          <span
            aria-hidden
            className="grid size-6 flex-none place-items-center rounded-md"
            style={{
              background: isActive ? 'rgba(var(--brand-rgb), 0.24)' : 'var(--elev)',
              color: isActive ? 'var(--brand-soft)' : 'var(--tx3)',
            }}
          >
            <Icon size={14} strokeWidth={2} />
          </span>
          {expanded && (
            <span
              className={cn('flex-1 truncate text-[13px] font-medium')}
              style={{ color: isActive ? 'var(--tx)' : 'var(--tx2)' }}
            >
              {item.label}
            </span>
          )}
        </>
      )}
    </Link>
  );
}
